import * as React from 'react';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GlobalSearchTab } from '../../../server/types/search-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { SearchDialog } from '../../shared/components/search/search.tsx';
import { useSearchEntityNavigation, type SearchEntityItem } from '../../shared/hooks/use-search-entity.ts';
import { GlobalSearchContext, type GlobalSearchContextType } from './global-search-bar-context.tsx';
import { GlobalSearchContent } from './global-search-content.tsx';

export type Props = {
    onBeforeOpenSearch: () => void;
    needsToCloseOtherWindowBeforeOpeningSearch?: boolean;
} & React.PropsWithChildren;

export const useGlobalSearch = (): GlobalSearchContextType => {
    const context = React.useContext(GlobalSearchContext);
    if (!context) {
        throw new Error('useGlobalSearch must be used within a GlobalSearchProvider');
    }
    return context;
};

export const GlobalSearchBarProvider: React.FC<Props> = ({
    children,
    onBeforeOpenSearch,
    needsToCloseOtherWindowBeforeOpeningSearch = true,
}) => {
    const { t } = useLocale();
    const [open, setOpen] = React.useState(false);
    const [defaultTab, setDefaultTab] = React.useState<GlobalSearchTab | undefined>(undefined);

    const openSearch = (initialTab?: GlobalSearchTab): void => {
        setDefaultTab(initialTab);
        onBeforeOpenSearch();
        if (needsToCloseOtherWindowBeforeOpeningSearch) {
            setTimeout(() => {
                setOpen(true);
            }, 500);
        } else {
            setOpen(true);
        }
    };

    const { navigateToEntity } = useSearchEntityNavigation();

    React.useEffect(() => {
        const down = (e: KeyboardEvent): void => {
            if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
                if (
                    (e.target instanceof HTMLElement && e.target.isContentEditable) ||
                    e.target instanceof HTMLInputElement ||
                    e.target instanceof HTMLTextAreaElement ||
                    e.target instanceof HTMLSelectElement
                ) {
                    return;
                }

                e.preventDefault();
                setOpen(true);
            }
        };

        document.addEventListener('keydown', down);

        return () => {
            document.removeEventListener('keydown', down);
        };
    }, []);

    const runCommand = React.useCallback((command: () => unknown) => {
        command();
        setOpen(false);
    }, []);

    const handleItemSelected = React.useCallback(({ id, entity }: SearchEntityItem) => {
        runCommand(() => {
            navigateToEntity({
                erp_id: id,
                entity: entity,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <GlobalSearchContext.Provider value={{ openSearch, open }}>
            <SearchDialog open={open} onOpenChange={setOpen} title={t('top_navbar__search-placeholder')}>
                <GlobalSearchContent
                    entities={[
                        SearchEntity.Customer,
                        SearchEntity.Product,
                        SearchEntity.SalesOrder,
                        SearchEntity.SalesQuote,
                    ]}
                    onItemSelected={handleItemSelected}
                    initialTab={defaultTab}
                    tracking={{ context: 'global' }}
                />
            </SearchDialog>
            {children}
        </GlobalSearchContext.Provider>
    );
};
